import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { User, LogOut, Save, Image as ImageIcon, Heart, Bookmark, Clock, LayoutDashboard } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useUserAuth } from "@/lib/user-auth";
import { useTheme } from "@/lib/theme-context";
import { useAuth } from "@/lib/auth";
import { useFavorites } from "@/lib/favorites";
import { useWatchlist } from "@/lib/watchlist";
import { useWatchHistory } from "@/lib/watch-history";
import { useAllItems } from "@/lib/library-store";
import { PosterCard } from "@/components/rawan/PosterCard";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "حسابي — Rawan UI" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AccountPage,
});

function AccountPage() {
  const { language } = useTheme();
  const t = (ar: string, en: string, fr: string) => (language === "ar" ? ar : language === "fr" ? fr : en);
  const { user, isAdmin, signOut, updateProfile } = useUserAuth();
  const { isAuthed: isLocalAdmin } = useAuth();
  const showDashboardLink = isAdmin || isLocalAdmin;
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) navigate({ to: "/login" });
    else {
      setDisplayName(user.displayName);
      setUsername(user.username);
      setAvatar(user.avatar);
    }
  }, [user, navigate]);

  const favs = useFavorites();
  const watch = useWatchlist();
  const history = useWatchHistory();
  const items = useAllItems();
  const favItems = favs.map((f) => items.find((i) => i.slug === f.slug)).filter(Boolean) as ReturnType<typeof useAllItems>;
  const watchItems = watch.map((f) => items.find((i) => i.slug === f.slug)).filter(Boolean) as ReturnType<typeof useAllItems>;
  // Show only the 3 most recent titles. De-duplicate by slug so watching
  // several episodes of the same series only surfaces once.
  const historyItems = (() => {
    const seen = new Set<string>();
    const out: ReturnType<typeof useAllItems> = [];
    for (const h of history) {
      if (seen.has(h.slug)) continue;
      const item = items.find((i) => i.slug === h.slug);
      if (!item) continue;
      seen.add(h.slug);
      out.push(item);
      if (out.length >= 3) break;
    }
    return out;
  })();

  if (!user) return null;

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    const res = await updateProfile({ displayName, username, avatar });
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    }
  }

  /**
   * Read the picked file, downscale it to a small square JPEG, and persist
   * it to Firebase immediately so it syncs across devices and shows on
   * every comment / avatar without waiting for the form submit.
   */
  async function onPickAvatar(file: File) {
    setUploadError(null);
    setUploading(true);
    try {
      const dataUrl = await compressAvatar(file, 256, 0.82);
      setAvatar(dataUrl);
      const res = await updateProfile({ avatar: dataUrl });
      if (!res.ok) {
        setUploadError(res.error || t("تعذّر رفع الصورة", "Could not upload image", "Impossible de téléverser l’image"));
      } else {
        setSaved(true);
        setTimeout(() => setSaved(false), 1800);
      }
    } catch {
      setUploadError(t("تعذّر قراءة الصورة", "Could not read image", "Impossible de lire l’image"));
    } finally {
      setUploading(false);
    }
  }

  async function onRemoveAvatar() {
    setAvatar(undefined);
    setUploading(true);
    try {
      await updateProfile({ avatar: undefined });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-5xl">
        <header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 sm:flex sm:justify-between mb-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative grid h-14 w-14 shrink-0 place-items-center rounded-2xl overflow-hidden" style={{ background: "var(--gradient-primary)" }}>
              {avatar ? (
                <img src={avatar} alt={displayName} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <User className="h-7 w-7 text-primary-foreground" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-xl sm:text-2xl font-extrabold">{user.displayName}</h1>
              <p className="truncate text-xs text-muted-foreground">@{user.username} · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {showDashboardLink && (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground text-sm transition"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">{t("لوحة التحكم","Dashboard","Tableau de bord")}</span>
              </Link>
            )}
            <button onClick={() => { signOut(); navigate({ to: "/" }); }} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground text-sm">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">{t("خروج","Logout","Déconnexion")}</span>
            </button>
          </div>
        </header>

        {/* Profile form */}
        <section className="bg-card border border-border rounded-2xl p-5 sm:p-6 shadow-card mb-8">
          <h2 className="text-lg font-extrabold mb-4">{t("إعدادات الملف الشخصي","Profile settings","Paramètres du profil")}</h2>
          <form onSubmit={onSave} className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold">{t("الاسم المعروض","Display name","Nom affiché")}</label>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                className="w-full mt-1.5 px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm font-bold">{t("اسم المستخدم","Username","Nom d’utilisateur")}</label>
              <input value={username} onChange={(e) => setUsername(e.target.value)}
                className="w-full mt-1.5 px-3 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-bold">{t("الصورة الرمزية","Avatar","Avatar")}</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickAvatar(f); }}
                className="hidden"
              />
              <div className="mt-1.5 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex w-full items-center justify-center gap-1.5 px-3 py-2.5 h-11 rounded-lg bg-background border border-border hover:bg-accent text-sm font-semibold disabled:opacity-60"
                >
                  <ImageIcon className="h-4 w-4" /> {uploading ? t("جارٍ الرفع...","Uploading...","Téléversement...") : t("اختر صورة","Choose image","Choisir une image")}
                </button>
                <button
                  type="submit"
                  className="btn-primary inline-flex w-full items-center justify-center gap-1.5 !px-3 !py-2.5 h-11 text-sm font-semibold"
                >
                  <Save className="h-4 w-4" /> {t("حفظ التعديلات","Save changes","Enregistrer")}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                <span>{t("البريد لا يمكن تعديله. الصورة تُحفظ مباشرة عند الاختيار.","Email cannot be edited. Avatar is saved on selection.","L’e-mail ne peut pas être modifié. L’avatar est enregistré à la sélection.")}</span>
                {avatar && (
                  <button type="button" onClick={onRemoveAvatar} className="text-muted-foreground hover:text-destructive">
                    حذف الصورة
                  </button>
                )}
                {saved && <span className="text-primary font-semibold">{t("تم الحفظ ✓","Saved ✓","Enregistré ✓")}</span>}
                {uploadError && <span className="text-destructive font-semibold">{uploadError}</span>}
              </div>
            </div>
          </form>
        </section>

        {/* Favorites */}
        <Block icon={<Heart className="h-5 w-5 text-primary" />} title={t("المفضلة","Favorites","Favoris")} empty={t("لا توجد عناصر بعد.","No items yet.","Aucun élément pour le moment.")} items={favItems} />
        <Block icon={<Bookmark className="h-5 w-5 text-primary" />} title={t("قائمة المشاهدة لاحقاً","Watch later","À regarder plus tard")} empty={t("لم تحفظ أي شيء بعد.","Nothing saved yet.","Rien d’enregistré pour le moment.")} items={watchItems} />
        <Block
          icon={<Clock className="h-5 w-5 text-primary" />}
          title={t("سجل المشاهدة","Watch history","Historique de visionnage")}
          empty={t("سيظهر هنا ما تابعت مشاهدته.","Your watched titles will appear here.","Vos titres visionnés apparaîtront ici.")}
          items={historyItems}
          extra={<Link to="/movies" className="text-sm text-primary hover:underline">{t("تصفح الأفلام →","Browse movies →","Parcourir les films →")}</Link>}
        />
      </main>
      <Footer />
    </div>
  );
}

function Block({ icon, title, empty, items, extra }: {
  icon: React.ReactNode; title: string; empty: string;
  items: ReturnType<typeof useAllItems>; extra?: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-lg font-extrabold">{title}</h3>
        </div>
        {extra}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-card border border-border rounded-xl p-5">{empty}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {items.slice(0, 10).map((m) => <PosterCard key={m.id} movie={m} />)}
        </div>
      )}
    </section>
  );
}

/**
 * Downscale an image File to a square JPEG data URL so Firestore can store
 * it inline (1MB doc limit). 256x256 @ q=0.82 → ~25-40KB.
 */
async function compressAvatar(file: File, size: number, quality: number): Promise<string> {
  const bitmap = await createImageBitmap(file).catch(async () => {
    // Safari < 15 fallback
    const url = URL.createObjectURL(file);
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = () => resolve(null);
      img.onerror = reject;
      img.src = url;
    });
    URL.revokeObjectURL(url);
    return img as unknown as ImageBitmap;
  });
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no_ctx");
  // Cover-crop the source into the target square.
  const sw = (bitmap as ImageBitmap).width;
  const sh = (bitmap as ImageBitmap).height;
  const scale = Math.max(size / sw, size / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  ctx.drawImage(bitmap as CanvasImageSource, (size - dw) / 2, (size - dh) / 2, dw, dh);
  return canvas.toDataURL("image/jpeg", quality);
}
